import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { replaceEqualDeep, useQuery, useQueryClient } from '@tanstack/react-query'
import cn from 'classnames'
import { Share2 } from 'lucide-react'
import api from 'api/api'
import useStore from 'state/knovStore'
import toast from 'utils/toast'
import { MarketEmbedConfig } from 'components/shared/linkPreviewUrls'
import {
    openPayloadOrdinalThread,
    payloadOrdinalIndicator,
    ORDINAL_THREAD_UNAVAILABLE_MESSAGE,
} from 'components/answers/Answer/payloadMediaMenu'
import {
    forgetOrdinalProvenance,
    loadOrdinalProvenance,
    peekOrdinalProvenance,
    refreshOrdinalProvenance,
} from 'components/answers/ordinalProvenance'
import { CLICK_SURFACE_EXEMPT_SELECTOR } from 'lib/suppressNextClick'
import { SATOSHI_UNIT, USD_UNIT } from 'lib/bsv-util'
import { ORDINAL_MARKET_QUERY_KEY } from 'state/ordinalStatePatch'
import { getAnswerQuestIds } from 'state/questAncestors'
import OrdinalsRender from 'components/answers/FileEmbed/OrdinalsRender'
import { ordinalMarketQueryOptions } from 'components/answers/FileEmbed/ordinalMarketQueryOptions'
import ArtifactMenu from 'components/answers/ArtifactMenu'
import SatsIcon from 'components/shared/SatsIcon'
import { ordinalPlaylistMediaKey } from 'components/answers/artifactMenuPolicy'
import OrdinalDetails from 'components/modals/OrdinalDetails'
import viewerStyles from 'components/modals/ordinals-viewer.module.scss'
import useOrdinalDescriptor from 'hooks/useOrdinalDescriptor'
import { useOrdinalSettlementStatus } from 'hooks/useOrdinalSettlementStatus'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import useOpenRight from 'refactor/hooks/useOpenRight'
import {
    currentUserCanTradeOrdinals,
    currentUserHasOrdinalsMarket,
} from 'components/panels/ordinalMarketFeature'
import { MarketConfirmTicket } from 'components/market/MarketOrderModals'
import UserIcon from 'components/users/UserIcon'
import type { IOrdinal, IOrdinalMarketSummary } from 'api/api'
import type { AnswerOrDraft } from 'types/answers'
import type { CombinedOrdinal } from 'types/ordinals'
import styles from './market-card-embed.module.scss'

interface MarketCardEmbedProps {
    config: MarketEmbedConfig
    className?: string
    /** Launches the answer share tray; the card's share button hides without it. */
    onShare?: () => void
    onContentLayoutSettled?: () => void
    /**
     * The card's own post. Accepting or declining from the card marks its
     * offer notification acted, the way the legacy offer card does.
     */
    answer?: AnswerOrDraft | null
}

/** The owner's open bids, stamped with the state revision they were read at. */
type OrderBookReading = { revision: number; activeBidIds: string[] }

// Copy per card kind. The beneficiary role doubles as the routing promise:
// upvalues on the card pay this person (see Answer#market_upvalue_beneficiary_id).
const VIEW_COPY = {
    // These are durable event receipts, not live status labels. Past-tense
    // copy stays true after a listing is cancelled, an offer is withdrawn, or
    // the buyer later resells the ordinal; the artwork rail carries live state.
    listing: {
        eyebrow: 'Listed',
        role: 'seller',
        counterpartyRole: null,
    },
    offer: {
        eyebrow: 'Offer made',
        role: 'bidder',
        counterpartyRole: 'owner',
    },
    sale: {
        eyebrow: 'Ordinal sold',
        role: 'buyer',
        counterpartyRole: 'seller',
    },
} as const

// Chrome inside the card that owns its click: nested controls, the market
// rail, and the media shells that take their own clicks (the video shell and
// the 3D/HTML loaders mark themselves data-no-modal-interaction, which the
// ordinals stream tile honours the same way). Most of these stop propagation
// themselves; this is the belt for anything that lets a click bubble. The
// stylesheet's hover-ring rule mirrors this list, so the ring never promises
// the thread where the click would not deliver it.
const NESTED_INTERACTIVE_SELECTOR = CLICK_SURFACE_EXEMPT_SELECTOR

// No text-selection check here: the card sits under the stream's
// `limit-selection` (user-select: none), so a selection can never start inside
// it, and a selection elsewhere on the page survives a click on it.
function frameClickIsClaimed(ev: React.MouseEvent<HTMLElement>): boolean {
    const hit = (ev.target as Element | null)?.closest?.(NESTED_INTERACTIVE_SELECTOR) || null
    return Boolean(hit && hit !== ev.currentTarget && ev.currentTarget.contains(hit))
}

const formatEventDate = (iso: string | null) => {
    if (!iso) return null
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * A market card rendered from a `market_embed` delta op (see
 * Ordinals::MarketAnnouncementService): the public stream face of a listing,
 * offer, or sale. Artwork and the standard market rail resolve live from the
 * ordinal descriptor; the receipt itself is static. For the ordinal's current
 * owner an open offer's receipt ends in Decline | Accept — the same actions
 * the rail's offers picker gives, one tap closer — and everywhere else the
 * share control rides the amount row. The answer's own upvalue controls make
 * the card upvaluable — routed server-side to the prominent beneficiary
 * (listing → seller, offer → bidder, sale → buyer). The embed
 * itself opens the ordinal's own system thread (Ordinals::CanonicalThread):
 * the frame is the pointer surface, the header eyebrow the keyboard control.
 * The thread pointer comes from the public provenance snapshot the artifact
 * menu already fetches for this origin, so it is live (a thread materialized
 * after the card was posted opens directly) and guest-visible; only a thread
 * that does not exist yet costs a write, which guests cannot make.
 */
export default function MarketCardEmbed({
    config,
    className,
    onShare,
    onContentLayoutSettled,
    answer,
}: MarketCardEmbedProps) {
    const { data: descriptor, isLoading } = useOrdinalDescriptor(config.origin, {
        assumeOrigin: true,
    })
    const { formatSatsAmount, formatSatsUsd, displayUnit } = useBsvDisplay()
    const { openRight } = useOpenRight()
    const openAuthModal = useStore(state => state.actions.modalActions.openAuthModal)
    const [detailsOpen, setDetailsOpen] = useState(false)

    const openOrdinalThread = async (ev: React.SyntheticEvent, source: 'frame' | 'header') => {
        ev.preventDefault()
        ev.stopPropagation()

        // A known pointer is stable and can open from the shared snapshot.
        // Refresh a missing pointer before treating the thread as absent:
        // another reader may have materialized it since this session cached it.
        let outcome = 'failed'
        try {
            const cachedProvenance = peekOrdinalProvenance(config.origin)
            let provenance = cachedProvenance || (await loadOrdinalProvenance(config.origin))
            if (
                cachedProvenance?.ok &&
                !(cachedProvenance.system_thread_answer_id && cachedProvenance.system_thread_id)
            ) {
                provenance = await refreshOrdinalProvenance(config.origin)
            }
            const isSignedIn = Boolean(gon?.currentUser)
            if (!provenance?.ok && !isSignedIn) {
                toast.error(ORDINAL_THREAD_UNAVAILABLE_MESSAGE)
            } else {
                outcome = await openPayloadOrdinalThread(
                    payloadOrdinalIndicator({
                        id: config.ordinalId,
                        system_thread_answer_id: provenance?.system_thread_answer_id,
                        system_thread_id: provenance?.system_thread_id,
                    }),
                    {
                        isSignedIn,
                        ensureOrdinalThread: api.ensureOrdinalThread,
                        openAuthModal: () => openAuthModal?.('signup', 'ordinal_thread'),
                        openRight: target =>
                            openRight(
                                { questId: target.questId, answerId: target.answerId },
                                { metricInputEvent: ev },
                            ),
                        onFailure: (failure, error) => {
                            console.error(
                                'Error opening the ordinal thread from a market card:',
                                failure,
                                error,
                            )
                            toast.error(ORDINAL_THREAD_UNAVAILABLE_MESSAGE)
                        },
                    },
                )
            }
        } catch (error) {
            console.error('Error opening the ordinal thread from a market card:', error)
            toast.error(ORDINAL_THREAD_UNAVAILABLE_MESSAGE)
        }
        // The snapshot now lags the thread it just created; the next reader
        // (this card, the artifact menu) refetches instead of writing again.
        if (outcome === 'ensured') forgetOrdinalProvenance(config.origin)

        // The card body used to bubble into the post's CLICK_ANSWER_GATE; this
        // is that click's replacement, so the surface stays measurable.
        window.logEv?.('CLICK_MARKET_CARD_THREAD', {
            source,
            outcome,
            market_view: config.view,
            order_id: config.orderId,
            bsv_ordinal_id: config.ordinalId,
        })
    }

    useEffect(() => {
        if (isLoading) return
        onContentLayoutSettled?.()
    }, [isLoading, onContentLayoutSettled, descriptor?.content_url])

    // --- Owner actions -----------------------------------------------------
    // Only the ordinal's current verified owner can act on an offer. The rail
    // inside the artwork observes the same market summary under the same
    // query key (live state events patch it), seeded from this reading so the
    // two share one request; the counterparty on the receipt (the owner at
    // offer time) stands in until it answers.
    const currentUserId = window.gon?.currentUser?.id
    const isOffer = config.view === 'offer'
    const marketEnabled = isOffer && !!currentUserId && currentUserHasOrdinalsMarket()
    const canTrade = marketEnabled && currentUserCanTradeOrdinals()
    const queryClient = useQueryClient()
    const marketQueryKey = [ORDINAL_MARKET_QUERY_KEY, config.ordinalId, currentUserId || null]
    const { data: market } = useQuery({
        queryKey: marketQueryKey,
        queryFn: async () => {
            return api.getOrdinalMarketStateFor(String(config.ordinalId))
        },
        ...ordinalMarketQueryOptions(null),
        enabled: marketEnabled,
    })
    // The rail's seed, frozen at the first reading: OrdinalsRender's memo
    // compares marketSummary by identity, so a live value would re-render the
    // artwork on every state event. Seeded, the rail skips its mount refetch
    // and reads the shared cache from then on.
    const marketSeedRef = useRef<{ ordinalId: string; market: IOrdinalMarketSummary } | null>(null)
    if (marketSeedRef.current && marketSeedRef.current.ordinalId !== config.ordinalId) {
        marketSeedRef.current = null
    }
    if (!marketSeedRef.current && market) {
        marketSeedRef.current = { ordinalId: config.ordinalId, market }
    }
    const marketSeed = marketSeedRef.current?.market ?? null
    const isOwner =
        market?.viewer?.is_owner ?? (!!currentUserId && config.counterparty?.id === currentUserId)

    // The receipt is a durable record of the offer being made, not of it
    // still standing: the bid may since have been withdrawn, declined,
    // replaced, or expired, and the summary only counts open bids. The
    // owner's order book (what the rail's offers picker reads) says whether
    // this order is still among them. It is read only while the summary
    // reports open bids, stamped with the revision it was read at, and
    // re-read once the summary moves past that stamp — the previous reading
    // stays on screen with its actions frozen while the re-read is in
    // flight, so the row never blinks and a stale affordance cannot fire.
    const [settlingOrderId, setSettlingOrderId] = useState<string | null>(null)
    const settlementPending = !!market?.settlement_pending || settlingOrderId === config.orderId
    const orderBookEnabled =
        marketEnabled && isOwner && (market?.bid_count || 0) > 0 && !settlementPending
    const orderBookKey = ['ordinalOrderBook', config.ordinalId, currentUserId || null]
    const {
        data: orderBook,
        isFetching: orderBookFetching,
        isError: orderBookErrored,
    } = useQuery({
        queryKey: orderBookKey,
        queryFn: async (): Promise<OrderBookReading> => {
            const res = await api.getOrdinalOrders(String(config.ordinalId))
            // The book comes with a summary at least as fresh as the shared
            // one (the read finalizes lazily expired bids); the summary's own
            // revision guard drops it if a state event got there first.
            if (res?.market) queryClient.setQueryData(marketQueryKey, res.market)
            return {
                revision: Number(res?.market?.state_revision ?? 0),
                activeBidIds: (res?.orders || [])
                    .filter(order => order.side === 'bid' && order.status === 'active')
                    .map(order => order.id),
            }
        },
        // A reading never goes backwards, whichever order the responses land in.
        structuralSharing: (previous: unknown, incoming: unknown) => {
            const old = previous as OrderBookReading | undefined
            const next = incoming as OrderBookReading
            if (old && old.revision > next.revision) return previous
            return replaceEqualDeep(previous, incoming)
        },
        enabled: orderBookEnabled,
        staleTime: 60_000,
    })
    const marketRevision = Number(market?.state_revision ?? 0)
    const orderBookStale = !!orderBook && orderBook.revision < marketRevision

    useEffect(() => {
        if (!orderBookEnabled) return
        // A current reading needs nothing; a missing one is either loading or
        // failed, and only the failure is worth another try.
        if (orderBook ? !orderBookStale : !orderBookErrored) return
        // Cards for one ordinal share the reading: the first to notice the
        // summary moved starts the re-read, the rest join it.
        if (queryClient.getQueryState(orderBookKey)?.fetchStatus === 'fetching') return
        void queryClient.invalidateQueries({ queryKey: orderBookKey })
        // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the revision, not the reading
    }, [
        orderBookEnabled,
        orderBookStale,
        orderBookErrored,
        marketRevision,
        config.ordinalId,
        currentUserId,
        queryClient,
    ])

    // Latched rather than filtered out of the reading: a re-read that started
    // before the decline committed could otherwise bring the order back for
    // one round trip.
    const [declinedOrderId, setDeclinedOrderId] = useState<string | null>(null)
    const showActions =
        orderBookEnabled &&
        !!orderBook?.activeBidIds.includes(config.orderId) &&
        declinedOrderId !== config.orderId
    const actionsFrozen = orderBookStale || orderBookFetching

    // Accepting starts a workflow that builds, may wait on the buyer's funds,
    // then broadcasts. Narrate it here the way the market rail does.
    const { settlementMessage, trackSettlement } = useOrdinalSettlementStatus()

    useEffect(() => {
        // The sale event patched the shared summary; the local flag only
        // bridged the gap until it did.
        if (settlingOrderId && market?.settlement_pending === false) setSettlingOrderId(null)
    }, [market?.settlement_pending, settlingOrderId])

    const [confirmOpen, setConfirmOpen] = useState(false)
    const [busy, setBusy] = useState<'accept' | 'decline' | null>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        // A different offer under the same component: nothing local carries over.
        setDeclinedOrderId(null)
        setSettlingOrderId(null)
        setConfirmOpen(false)
        setError(null)
    }, [config.orderId])

    // The ticket belongs to the action row: when the row goes (the bid was
    // withdrawn, another sale started, the ordinal changed hands) the ticket
    // closes, unless an action is mid-flight and its outcome decides. An error
    // outlives the row — it explains why the row went — and clears once the
    // row is back.
    const rowWasShowing = useRef(showActions)
    useEffect(() => {
        const wasShowing = rowWasShowing.current
        rowWasShowing.current = showActions
        if (showActions && !wasShowing) setError(null)
        if (!showActions && !busy) setConfirmOpen(false)
    }, [showActions, busy])

    const setMarket = (next: IOrdinalMarketSummary | null | undefined) => {
        // The rail over the artwork reads the same summary; hand it the
        // response so it does not wait on the state event.
        if (next) queryClient.setQueryData(marketQueryKey, next)
        else queryClient.invalidateQueries({ queryKey: marketQueryKey })
    }

    const markNotificationActed = async () => {
        // The notification hangs off the quest the server keys it on
        // (Answer#quest_or_child_quest): the containing quest, else the quest
        // the card heads — a market card is a root head, so the latter.
        const { uptreeQuestId, downtreeQuestId } = getAnswerQuestIds(answer)
        const questId = uptreeQuestId ?? downtreeQuestId
        if (!questId || !answer?.id) return
        try {
            await api.actOnNoti(questId, answer.id)
        } catch (err) {
            console.warn('Could not mark the offer notification acted:', err)
        }
    }

    // A successful action unmounts the control that held focus (the row, and
    // the ticket with it), which would drop keyboard focus to the body. The
    // eyebrow is the card's stable control, so focus moves there — only when
    // focus was in the card or its ticket to begin with, read before the
    // busy state disables the pressed button (a disabled button loses focus).
    const frameRef = useRef<HTMLDivElement>(null)
    const eyebrowRef = useRef<HTMLButtonElement>(null)
    const focusIsInCard = () => {
        const active = document.activeElement
        return (
            active instanceof HTMLElement &&
            (!!frameRef.current?.contains(active) || !!active.closest('[role="dialog"]'))
        )
    }

    const runAction = async (action: 'accept' | 'decline') => {
        const focusWasInCard = focusIsInCard()
        setBusy(action)
        setError(null)
        try {
            if (action === 'accept') {
                // The order as the owner saw it on the card: a local-signing
                // owner's device signs against this intent, not the server's.
                const result = await api.settleOrdinalOrder(
                    String(config.ordinalId),
                    String(config.orderId),
                    { side: 'bid', price_sats: Number(config.priceSats) },
                )
                setSettlingOrderId(config.orderId)
                trackSettlement(result)
                setMarket(result?.market)
                setConfirmOpen(false)
            } else {
                const result = await api.declineOrdinalOrder(
                    String(config.ordinalId),
                    String(config.orderId),
                )
                setDeclinedOrderId(config.orderId)
                setMarket(result?.market)
            }
            if (focusWasInCard) eyebrowRef.current?.focus({ preventScroll: true })
            void markNotificationActed()
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not update this offer.')
            // A refusal usually means the book moved under a missed state
            // event (a withdrawal, a lazy expiry): re-read both so the row
            // catches up instead of offering the same tap again.
            setMarket(null)
            void queryClient.invalidateQueries({ queryKey: orderBookKey })
        } finally {
            setBusy(null)
        }
    }

    const stop = (ev: React.SyntheticEvent) => {
        ev.stopPropagation()
    }

    // What the receipt says once the owner has acted here: the sale's
    // progress, or that the offer was turned down (the row alone vanishing
    // would read like a withdrawal).
    const statusLine =
        settlingOrderId === config.orderId
            ? settlementMessage || 'Sale in progress…'
            : declinedOrderId === config.orderId
            ? 'Offer declined'
            : null

    const copy = VIEW_COPY[config.view] || VIEW_COPY.sale
    // "⚡ 20,000,000" — the bolt is the unit when the amount reads in sats;
    // BSV and USD readings keep their unit text. A dollar reading echoes the
    // sats (bolt again); a sats or BSV reading echoes the dollars.
    const priceUnit = displayUnit(config.priceSats)
    const priceInSats = priceUnit === SATOSHI_UNIT
    const priceMain = formatSatsAmount(config.priceSats, {
        compactSats: false,
        includeUnit: !priceInSats,
    })
    const priceEchoInSats = priceUnit === USD_UNIT
    const priceEcho = priceEchoInSats
        ? Number(config.priceSats).toLocaleString()
        : formatSatsUsd(config.priceSats)
    // The owner's buttons name what they act on, in the offer's own sats.
    const offerLabel = `${Number(config.priceSats).toLocaleString()} sats${
        config.beneficiary?.name ? ` from ${config.beneficiary.name}` : ''
    }`
    // The event stamp at the header's right edge, opposite the eyebrow: the
    // event's date, and a sale's ordinal number ("SALE #2 · AUG 31"). The
    // eyebrow already names the event, so the stamp does not repeat it.
    const eventMeta = [
        config.view === 'sale' && config.saleNumber && config.saleNumber > 1
            ? `Sale #${config.saleNumber}`
            : null,
        formatEventDate(config.eventAt),
    ]
        .filter(Boolean)
        .join(' · ')

    // Images crop gracefully into the fixed artwork band, but a cover-cropped
    // video frame loses its subject (the band is far wider than 16:9). Show
    // AV frames whole over a blurred cover backdrop instead.
    const artworkContentType = (descriptor?.content_type || '').toLowerCase()
    const containArtwork =
        artworkContentType.startsWith('video/') || artworkContentType.startsWith('audio/')

    const playlistMediaKey = ordinalPlaylistMediaKey({
        contentType: descriptor?.content_type,
        bsvOrdinalId: config.ordinalId,
        outpoint: descriptor?.origin_outpoint || config.origin,
        src: descriptor?.content_url,
    })

    // Same synthesis as ArtifactBadge: OrdinalDetails wants a CombinedOrdinal,
    // the card only holds an origin outpoint plus a lazily-resolved descriptor.
    const detailsOrdinal = useMemo<CombinedOrdinal | null>(() => {
        if (!config.origin) return null
        const [txid, vout = '0'] = config.origin.split('_')
        const ordinalId = descriptor?.bsv_ordinal_id || config.ordinalId || null

        return {
            txid: txid || '',
            vout: Number(vout || 0),
            outpoint: config.origin,
            utxo_outpoint: config.origin,
            origin: {
                outpoint: config.origin,
                data: {
                    insc: {
                        file: {
                            type: descriptor?.content_type || 'application/octet-stream',
                        },
                    },
                    map: { name: config.origin },
                },
            },
            treechatData: ordinalId
                ? ({
                      id: ordinalId,
                      preview_url: descriptor?.preview_url || config.previewUrl,
                  } as IOrdinal)
                : undefined,
            descriptor: descriptor || undefined,
            content_type: descriptor?.content_type || undefined,
            status: 'confirmed',
        }
    }, [config.ordinalId, config.origin, config.previewUrl, descriptor])

    // A sats reading: the bolt stands in for the unit word, which stays in
    // the markup for screen readers.
    const satsReading = (text: string, inSats: boolean) => (
        <>
            {inSats && <SatsIcon inline contextStyles={styles.priceIcon} />}
            {text}
            {inSats && <span className={styles.priceUnit}> sats</span>}
        </>
    )

    // The share control, a flat circle: in the right margin of the amount row,
    // or of the action row when the owner has one.
    const shareButton = onShare ? (
        <button
            type="button"
            className={styles.shareButton}
            aria-label="Share this card"
            onClick={ev => {
                ev.stopPropagation()
                onShare()
            }}
        >
            <Share2 size={16} strokeWidth={2.4} />
        </button>
    ) : null

    return (
        <>
            <div
                className={cn(styles.container, className)}
                data-testid="market-card"
                data-market-view={config.view}
            >
                {/* The frame is the ordinal thread's surface everywhere the card
                    renders, embeds included: the exemption keeps PostEmbed's
                    permalink capture handler off it, stopPropagation keeps the
                    child-quest bubble handler off it, and a quoted card's own
                    post stays reachable from the gutter around the frame. */}
                <div
                    ref={frameRef}
                    className={styles.embedFrame}
                    data-testid="market-card-thread-surface"
                    data-post-embed-navigation-exempt="true"
                    onClick={ev => {
                        if (frameClickIsClaimed(ev)) {
                            // The nested target already received the click.
                            // Keep passthrough media surfaces from bubbling it
                            // into the enclosing announcement post.
                            ev.stopPropagation()
                            return
                        }
                        void openOrdinalThread(ev, 'frame')
                    }}
                >
                    <div className={styles.header}>
                        <button
                            ref={eyebrowRef}
                            type="button"
                            className={styles.headerTitle}
                            data-testid="market-card-open-thread"
                            aria-label={`${copy.eyebrow}: open the ordinal's thread`}
                            onClick={ev => void openOrdinalThread(ev, 'header')}
                        >
                            {copy.eyebrow}
                        </button>
                        {eventMeta && <span className={styles.eventMeta}>{eventMeta}</span>}
                    </div>
                    <div
                        className={cn(styles.artwork, containArtwork && styles.artworkContain)}
                        data-market-artwork
                    >
                        {containArtwork && descriptor?.preview_url && (
                            <div
                                className={styles.artworkBackdrop}
                                style={{ backgroundImage: `url(${descriptor.preview_url})` }}
                                aria-hidden="true"
                            />
                        )}
                        {descriptor?.content_url ? (
                            <OrdinalsRender
                                outpoint={descriptor.origin_outpoint || config.origin}
                                url={descriptor.content_url}
                                bsvOrdinalId={config.ordinalId}
                                artifactOrdinalId={config.ordinalId}
                                contentType={descriptor.content_type || undefined}
                                filename={descriptor.filename || undefined}
                                previewUrl={descriptor.preview_url || undefined}
                                previewSize="list"
                                allowInteraction={false}
                                showBidAsk={true}
                                marketSummary={marketSeed}
                                fillVideoContainer={containArtwork}
                                className={styles.ordinalRender}
                                title="Ordinal"
                            />
                        ) : (
                            <div className={styles.artworkFallback}>
                                {isLoading ? 'Loading artwork…' : 'Ordinal'}
                            </div>
                        )}
                        <ArtifactMenu
                            answer={{}}
                            origin={config.origin}
                            onDetails={() => setDetailsOpen(true)}
                            onOpenStream={() => openRight({ ordinals: true, order: 'time' })}
                            playlistMediaKey={playlistMediaKey}
                        />
                    </div>
                    <div className={styles.receipt}>
                        {(config.beneficiary || config.counterparty) && (
                            <div className={styles.partyRow}>
                                {config.beneficiary && (
                                    <div className={styles.party}>
                                        <UserIcon
                                            user={config.beneficiary}
                                            size="small"
                                            contextStyles={styles.partyAvatar}
                                        />
                                        <div className={styles.partyMeta}>
                                            <span className={styles.partyRole}>
                                                {copy.role} · earns upvalues
                                            </span>
                                            <span className={styles.partyName}>
                                                {config.beneficiary.name}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {config.counterparty && copy.counterpartyRole && (
                                    <div className={cn(styles.party, styles.counterparty)}>
                                        <div
                                            className={cn(
                                                styles.partyMeta,
                                                styles.counterpartyMeta,
                                            )}
                                        >
                                            <span
                                                className={cn(
                                                    styles.partyRole,
                                                    styles.counterpartyRole,
                                                )}
                                            >
                                                {copy.counterpartyRole}
                                            </span>
                                            <span className={styles.partyName}>
                                                {config.counterparty.name}
                                            </span>
                                        </div>
                                        <UserIcon
                                            user={config.counterparty}
                                            size="small"
                                            contextStyles={styles.partyAvatar}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        <div className={styles.moneyRow}>
                            <span className={styles.amount}>
                                <span className={styles.price}>
                                    {satsReading(priceMain, priceInSats)}
                                </span>
                                {priceEcho && (
                                    <span className={styles.priceEcho}>
                                        {satsReading(priceEcho, priceEchoInSats)}
                                    </span>
                                )}
                            </span>
                            {/* Without an action row the share circle keeps
                                the row's right margin; a narrow card drops it
                                to its own line instead of squeezing the
                                amount. */}
                            {!showActions && shareButton}
                        </div>
                        {statusLine && (
                            <div className={styles.statusLine} role="status">
                                {statusLine}
                            </div>
                        )}
                        {error && !confirmOpen && (
                            <div className={styles.actionError} role="alert">
                                {error}
                            </div>
                        )}
                        {showActions && (
                            <div className={styles.actionRow} data-answer-interactive="true">
                                <button
                                    type="button"
                                    className={cn(styles.actionButton, styles.declineButton)}
                                    aria-label={`Decline the offer of ${offerLabel}`}
                                    disabled={!!busy || actionsFrozen}
                                    onClick={ev => {
                                        stop(ev)
                                        void runAction('decline')
                                    }}
                                >
                                    {busy === 'decline' ? 'Declining…' : 'Decline'}
                                </button>
                                {canTrade && (
                                    <button
                                        type="button"
                                        className={cn(styles.actionButton, styles.acceptButton)}
                                        aria-label={`Accept the offer of ${offerLabel}`}
                                        disabled={!!busy || actionsFrozen}
                                        onClick={ev => {
                                            stop(ev)
                                            setError(null)
                                            setConfirmOpen(true)
                                        }}
                                    >
                                        {busy === 'accept' ? 'Accepting…' : 'Accept'}
                                    </button>
                                )}
                                {shareButton}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {confirmOpen && canTrade && (
                <MarketConfirmTicket
                    kind="accept"
                    origin={config.origin}
                    priceSats={Number(config.priceSats)}
                    listPriceSats={market?.ask?.price_sats ?? null}
                    bidderName={config.beneficiary?.name || null}
                    busy={busy === 'accept'}
                    error={error}
                    onConfirm={() => void runAction('accept')}
                    onClose={() => setConfirmOpen(false)}
                />
            )}

            {detailsOpen &&
                detailsOrdinal &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className={viewerStyles.lightbox}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Ordinal details"
                        onClick={event => {
                            if (event.target === event.currentTarget) setDetailsOpen(false)
                        }}
                    >
                        <OrdinalDetails
                            selectedOrdinal={{
                                ordinal: detailsOrdinal,
                                content: null,
                                isLoading: false,
                                error: null,
                                forceLive: false,
                            }}
                            inscriptionOutpoint={config.origin}
                            onClose={() => setDetailsOpen(false)}
                            onSendClick={() => undefined}
                            showShowInProfile={false}
                            showHidden={false}
                            showSend={false}
                        />
                    </div>,
                    document.body,
                )}
        </>
    )
}
